import { Permissions } from "@/auth/permissions";
import { TICKET_LIST_VIEW_DEFINITIONS } from "@/features/tickets/model/ticket-list-views";
import type { TranslationKey } from "@/locales/i18n";

/** 侧栏角标绑定的工单计数视图；取值对应后端 TicketCountSummaryDto.views 的字段名。 */
export type NavigationCountKey = "all" | "assignedToMe" | "participatingByMe" | "unassigned" | "uncategorized";

export type NavigationItem = {
  labelKey: TranslationKey;
  icon: string;
  active?: boolean;
  badge?: string;
  /** 声明后由 `useNavigationBadges` 填入实时数量；不声明则不显示角标。 */
  countKey?: NavigationCountKey;
  href?: string;
  /**
   * 展示该入口所需的权限，任一命中即可见（any-of）。
   * 不填 = 所有登录用户可见。前端隐藏只是体验，真正判权在服务端。
   */
  permissions?: string[];
};

export type NavigationGroup = {
  labelKey: TranslationKey;
  items: NavigationItem[];
};

/** 系统配置区任一子项可见，工作台侧栏才显示「系统配置」入口、`/settings` 才放行。 */
export const SETTINGS_PERMISSIONS = [
  Permissions.projects.view,
  Permissions.categories.view,
  Permissions.routingRules.view,
  Permissions.teams.view,
  Permissions.agents.view,
  Permissions.settings.view,
  Permissions.settings.manageAi,
];

export const navigationGroups: NavigationGroup[] = [
  {
    labelKey: "navigation.group.myViews",
    items: [
      {
        labelKey: TICKET_LIST_VIEW_DEFINITIONS.all.labelKey,
        icon: "tabler:ticket",
        countKey: "all",
        href: TICKET_LIST_VIEW_DEFINITIONS.all.path,
        permissions: [Permissions.tickets.view],
      },
      {
        labelKey: TICKET_LIST_VIEW_DEFINITIONS.assigned.labelKey,
        icon: "tabler:user-check",
        countKey: "assignedToMe",
        href: TICKET_LIST_VIEW_DEFINITIONS.assigned.path,
        permissions: [Permissions.tickets.view],
      },
      {
        labelKey: TICKET_LIST_VIEW_DEFINITIONS.unassigned.labelKey,
        icon: "tabler:calendar-question",
        countKey: "unassigned",
        href: TICKET_LIST_VIEW_DEFINITIONS.unassigned.path,
        permissions: [Permissions.tickets.view],
      },
      {
        labelKey: TICKET_LIST_VIEW_DEFINITIONS.participating.labelKey,
        icon: "tabler:heart-handshake",
        countKey: "participatingByMe",
        href: TICKET_LIST_VIEW_DEFINITIONS.participating.path,
        permissions: [Permissions.tickets.view],
      },
      {
        labelKey: TICKET_LIST_VIEW_DEFINITIONS.uncategorized.labelKey,
        icon: "tabler:tags",
        countKey: "uncategorized",
        href: TICKET_LIST_VIEW_DEFINITIONS.uncategorized.path,
        permissions: [Permissions.tickets.view],
      },
      // 「最近解决」按解决时间倒序；计数接口没有时间窗口维度，先不挂角标。
      {
        labelKey: TICKET_LIST_VIEW_DEFINITIONS["recently-resolved"].labelKey,
        icon: "tabler:circle-check",
        href: TICKET_LIST_VIEW_DEFINITIONS["recently-resolved"].path,
        permissions: [Permissions.tickets.view],
      },
      {
        labelKey: "navigation.item.notifications",
        icon: "tabler:bell",
        href: "/notifications",
      },
    ],
  },
  {
    labelKey: "navigation.group.analytics",
    items: [
      { labelKey: "navigation.item.overview", icon: "tabler:layout-dashboard", href: "/overview", permissions: [Permissions.reports.view] },
      { labelKey: "navigation.item.customers", icon: "tabler:address-book", href: "/customers", permissions: [Permissions.customers.view] },
      // 「SLA 监控 / 服务质量 / 工作量分析」三个占位项已删除：前两者还没有数据支撑（无 SLA 策略、无满意度来源），
      // 工作量并入报表页的第二个标签页。点不动的菜单项是负资产，等能力落地再加回来。
      { labelKey: "navigation.item.reports", icon: "tabler:report-analytics", href: "/reports", permissions: [Permissions.reports.view] },
    ],
  },
  {
    labelKey: "navigation.group.system",
    items: [{ labelKey: "navigation.item.systemConfiguration", icon: "tabler:settings", href: "/settings", permissions: SETTINGS_PERMISSIONS }],
  },
];

export const systemConfigurationNavigation: NavigationItem[] = [
  { labelKey: "navigation.item.projects", icon: "tabler:box", href: "/settings/projects", permissions: [Permissions.projects.view] },
  { labelKey: "navigation.item.categories", icon: "tabler:tag", href: "/settings/ticket-categories", permissions: [Permissions.categories.view] },
  { labelKey: "navigation.item.routingRules", icon: "tabler:route", href: "/settings/routing-rules", permissions: [Permissions.routingRules.view] },
  {
    labelKey: "navigation.item.organization",
    icon: "tabler:users",
    href: "/settings/organization",
    permissions: [Permissions.teams.view, Permissions.agents.view],
  },
  { labelKey: "navigation.item.ai", icon: "logos:google-gemini-icon", href: "/settings/ai", permissions: [Permissions.settings.manageAi] },
  { labelKey: "navigation.item.systemSettings", icon: "tabler:settings", href: "/settings/system", permissions: [Permissions.settings.view] },
];

/** Use the same grouped sidebar pattern as the ticket workbench. */
export const systemConfigurationGroups: NavigationGroup[] = [
  { labelKey: "settings.navigation.ticketing", items: systemConfigurationNavigation.slice(0, 3) },
  { labelKey: "settings.navigation.operations", items: systemConfigurationNavigation.slice(3, 5) },
  { labelKey: "navigation.group.system", items: systemConfigurationNavigation.slice(5) },
];
